import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";

export default function PreviewCourse() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token");
  const token = tokenFromUrl || localStorage.getItem("sol_preview_token");
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("No preview token found. Sign up below to unlock Module 1.");
      return;
    }
    api
      .get(`/preview/course?token=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Your preview link is invalid or expired. Sign up again to unlock Module 1."));
  }, [token]);

  if (error) {
    return (
      <Layout>
        <section className="container-narrow py-32 text-center">
          <p className="text-brand-inkMuted">{error}</p>
          <Link
            to="/preview"
            className="inline-block mt-6 text-brand-rust hover:underline"
            data-testid="preview-back-to-signup"
          >
            ← Back to preview signup
          </Link>
        </section>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <section className="container-narrow py-32 text-center text-brand-inkMuted">
          Loading Module 1…
        </section>
      </Layout>
    );
  }

  const lessons = data.module.lessons || [];
  const lesson = lessons[activeIdx] || lessons[0];

  return (
    <Layout>
      <section className="border-b border-brand-line">
        <div className="container-narrow py-12 md:py-16">
          <span className="overline text-brand-rust">Free preview · {data.course_title}</span>
          <h1
            className="font-display text-4xl md:text-5xl mt-3 text-brand-ink leading-[1.08]"
            data-testid="preview-module-title"
          >
            {data.module.title}
          </h1>
          {data.lead_name && (
            <p className="mt-3 text-brand-inkMuted">
              Welcome, {data.lead_name}. Take your time with these four lessons.
            </p>
          )}
        </div>
      </section>

      <section className="bg-white">
        <div className="container-narrow py-12 md:py-16 grid lg:grid-cols-12 gap-12">
          {/* Lessons sidebar */}
          <aside className="lg:col-span-4" data-testid="preview-lessons-sidebar">
            <div className="overline text-brand-inkMuted mb-4">
              {lessons.length} lessons
            </div>
            <ol className="space-y-1">
              {lessons.map((l, i) => {
                const active = i === activeIdx;
                return (
                  <li key={l.title}>
                    <button
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      data-testid={`preview-lesson-tab-${i}`}
                      className={`w-full text-left p-4 border-l-2 transition-colors ${
                        active
                          ? "border-brand-rust bg-brand-bg"
                          : "border-transparent hover:border-brand-line hover:bg-brand-bg/60"
                      }`}
                    >
                      <div className="text-xs uppercase tracking-[.16em] text-brand-inkMuted">
                        Lesson {i + 1} · {l.duration}
                      </div>
                      <div className="mt-1 font-display text-lg text-brand-ink">
                        {l.title}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Active lesson */}
          <article className="lg:col-span-8" data-testid="preview-lesson-body">
            <div className="overline text-brand-rust">
              Lesson {activeIdx + 1} of {lessons.length} · {lesson.duration}
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 text-brand-ink leading-tight">
              {lesson.title}
            </h2>

            {/* Video placeholder — visible only when a real video URL is set */}
            {lesson.video_url && !lesson.video_url.includes("dQw4w9WgXcQ") && (
              <div className="mt-8 aspect-video bg-black">
                <iframe
                  title={lesson.title}
                  src={lesson.video_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            <div className="mt-8 prose prose-lg max-w-none text-brand-ink leading-relaxed whitespace-pre-line">
              {lesson.content}
            </div>

            {/* Lesson nav */}
            <div className="mt-12 pt-8 border-t border-brand-line flex items-center justify-between">
              <button
                type="button"
                disabled={activeIdx === 0}
                onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                data-testid="preview-prev-lesson"
                className="text-sm text-brand-inkMuted hover:text-brand-ink disabled:opacity-30"
              >
                ← Previous lesson
              </button>
              <button
                type="button"
                disabled={activeIdx >= lessons.length - 1}
                onClick={() => setActiveIdx((i) => Math.min(lessons.length - 1, i + 1))}
                data-testid="preview-next-lesson"
                className="text-sm text-brand-ink hover:text-brand-rust disabled:opacity-30"
              >
                Next lesson →
              </button>
            </div>
          </article>
        </div>
      </section>

      {/* Upgrade CTA */}
      <section className="bg-brand-ink text-white py-20 md:py-28" data-testid="preview-upgrade-cta">
        <div className="container-narrow text-center">
          <span className="overline text-brand-rust">When you&apos;re ready</span>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">
            The remaining {data.total_modules - 1} modules.
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto leading-relaxed">
            {data.total_lessons - lessons.length} more lessons. Module quizzes, a final exam at 80% to pass, and a Certificate of Completion. Lifetime access — no subscriptions, no annual renewal.
          </p>
          <Link
            to={`/p/${data.course_id}`}
            data-testid="preview-upgrade-link"
            className="inline-block mt-10 bg-white text-brand-ink hover:bg-brand-bg px-8 py-4 text-sm font-medium tracking-wide transition-colors"
          >
            See the full course — ${data.full_course_price?.toFixed(0)} →
          </Link>
        </div>
      </section>
    </Layout>
  );
}
